import React from 'react';
import { Box, Typography, Card, CardContent } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import { analyticsService } from '../../services/analyticsService';

interface DetailItem {
  label: string;
  value: number | string;
}

interface MetricCardProps {
  title: string;
  value: number | string;
  change?: number;
  icon: React.ReactNode;
  color: string;
  format?: 'number' | 'percentage' | 'time' | 'rating';
  details?: DetailItem[];
  onClick?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  icon,
  color,
  format = 'number',
  details,
  onClick,
}) => {
  const formatValue = (val: number | string) => {
    if (typeof val === 'string') return val;

    switch (format) {
      case 'percentage':
        return `${val.toFixed(1)}%`;
      case 'time':
        return `${val.toFixed(2)}s`;
      case 'rating':
        return `${val.toFixed(1)}/5`;
      default:
        return val.toLocaleString();
    }
  };

  const getTrendIcon = () => {
    if (change === undefined) return null;
    return change >= 0 ? <TrendingUp /> : <TrendingDown />;
  };

  const getTrendColor = () => {
    if (change === undefined) return 'text.secondary';
    return change >= 0 ? 'success.main' : 'error.main';
  };

  return (
    <Card
      sx={{ height: '100%', cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box sx={{ color, fontSize: '2rem' }}>{icon}</Box>
          {change !== undefined && (
            <Box display="flex" alignItems="center" sx={{ color: getTrendColor() }}>
              {getTrendIcon()}
              <Typography variant="body2" sx={{ ml: 0.5 }}>
                {analyticsService.formatPercentageChange(change)}
              </Typography>
            </Box>
          )}
        </Box>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          {formatValue(value)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>

        {details && details.length > 0 && (
          <Box mt={2}>
            {details.map((d, idx) => (
              <Box key={idx} display="flex" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  {d.label}
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {typeof d.value === 'number' ? d.value.toLocaleString() : d.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default MetricCard;
